import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import LeadgenLoading from '../../ui/LeadgenLoading.js';
import Banner from '../../ui/Banner';
import Pagination from '../../ui/Pagination';
import Toast from '../../ui/Toast';
import VisibilitySelect from '../../ui/VisibilitySelect';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import './AnalysisReportView.css';

const AnalysisReportView = ({ userId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const navigate = useNavigate();
  const { workspaceRole, workspaceType } = useWorkspace();
  const isOrganization = workspaceType === 'organization';
  const canManageVisibility =
    isOrganization && (workspaceRole === 'org_owner' || workspaceRole === 'org_admin');

  // Search, Filter, Sort State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    fetchReports();
  }, [userId]);

  const fetchReports = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get('/market-analysis');
      if (response.data.success) {
        setReports(response.data.data);
      } else {
        setReports([]);
      }
    } catch (err) {
      console.error('Error fetching analysis reports:', err);
      if (showLoading)
        setToast({
          show: true,
          message: 'Failed to load analysis reports. Please try again later.',
          type: 'error',
        });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Polling for processing reports
  useEffect(() => {
    const hasProcessing = reports.some((r) => r.status === 'processing');
    let interval;

    if (hasProcessing) {
      interval = setInterval(() => {
        fetchReports(false);
      }, 5000);
    }

    return () => clearInterval(interval);
  }, [reports]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedReports = useMemo(() => {
    let result = [...reports];

    // Search
    if (searchTerm) {
      result = result.filter((r) => r.query.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Status Filter
    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [reports, searchTerm, statusFilter, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedReports.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedReports = useMemo(() => {
    const indexOfLastReport = currentPage * itemsPerPage;
    const indexOfFirstReport = indexOfLastReport - itemsPerPage;
    return filteredAndSortedReports.slice(indexOfFirstReport, indexOfLastReport);
  }, [filteredAndSortedReports, currentPage, itemsPerPage]);

  const columns = useMemo(
    () => [
      { key: 'created_at', label: 'Date', sortable: true, width: '20%' },
      { key: 'query', label: 'Query', sortable: true, width: '35%' },
      { key: 'status', label: 'Status', width: '12%' },
      ...(canManageVisibility ? [{ key: 'visibility', label: 'Visibility', width: '15%' }] : []),
      { key: 'action', label: 'Action', width: '18%' },
    ],
    []
  );

  const handleVisibilityChange = async (reportId, newVisibility) => {
    try {
      const response = await axios.patch(`/market-analysis/${reportId}/visibility`, {
        visibility: newVisibility,
      });
      if (response.data.success) {
        setReports((prev) =>
          prev.map((r) => (r.id === reportId ? { ...r, visibility: newVisibility } : r))
        );
        setToast({
          show: true,
          message: `Report visible to ${newVisibility === 'everyone' ? 'Everyone' : 'Admins Only'}`,
          type: 'success',
        });
      }
    } catch (err) {
      console.error('Error updating visibility:', err);
      setToast({ show: true, message: 'Failed to update visibility.', type: 'error' });
    }
  };

  const handleDownload = async (reportId, filename) => {
    try {
      // Use the download endpoint
      const response = await axios({
        url: `/market-analysis/${reportId}/download/full`,
        method: 'GET',
        responseType: 'blob', // Important for handling PDF data
      });

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const getTopicSlug = (topic) => {
        return (topic || 'Research')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      };

      const report = reports.find((r) => r.id === reportId);
      const date = new Date(report?.created_at || Date.now()).toISOString().split('T')[0];
      const topic = report?.report_title || report?.query || 'Research';
      const filename = `Market-Research_${getTopicSlug(topic)}_${date}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setToast({
        show: true,
        message: 'Failed to download PDF. The report might still be processing or failed.',
        type: 'error',
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="ar-page">
      <Banner
        type="page-header"
        title="Analysis Report"
        subtitle="Review and download your previous market analysis PDFs."
      />

      <div className="ar-toolbar">
        <div className="ar-search">
          <i className="fas fa-search ar-search-icon" aria-hidden></i>
          <input
            type="text"
            placeholder="Search by query..."
            className="ar-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="ar-search-clear"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="ar-toolbar-actions">
          <select
            className="ar-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
          </select>
          <button
            className="ar-refresh-btn"
            onClick={() => fetchReports()}
            title="Refresh list"
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Refresh
          </button>
        </div>
      </div>

      <Pagination
        className="ar-pagination-card"
        columns={columns}
        data={paginatedReports}
        renderRow={(report) => (
          <tr key={report.id}>
            <td>{formatDate(report.created_at)}</td>
            <td className="ar-query-cell" title={report.query}>
              {report.query}
            </td>
            <td>
              <span className={`ar-status ar-status-${report.status}`}>
                {report.status === 'processing' && (
                  <i className="fas fa-circle-notch fa-spin status-spinner"></i>
                )}
                {report.status}
              </span>
            </td>
            {canManageVisibility && (
              <td>
                <VisibilitySelect
                  value={report.visibility === 'everyone' ? 'everyone' : 'admins'}
                  onChange={(val) => handleVisibilityChange(report.id, val)}
                  options={[
                    { value: 'admins', label: 'Admins Only', icon: 'fas fa-shield-alt' },
                    { value: 'everyone', label: 'Everyone', icon: 'fas fa-globe' },
                  ]}
                />
              </td>
            )}
            <td>
              {report.status === 'completed' ? (
                <button
                  className="ar-btn ar-btn-primary"
                  onClick={() => handleDownload(report.id, report.query)}
                >
                  <i className="fas fa-file-pdf"></i> Download PDF
                </button>
              ) : report.status === 'processing' ? (
                <button
                  className="ar-btn ar-btn-accent"
                  onClick={() => {
                    if (report.chat_room_id) {
                      navigate(`/discover/${report.chat_room_id}`);
                    } else {
                      navigate('/market-analysis', { state: { initialAnalysisId: report.id } });
                    }
                  }}
                >
                  <i className="fas fa-chart-line"></i> View Progress
                </button>
              ) : (
                <span className="ar-not-available">Report Failed</span>
              )}
            </td>
          </tr>
        )}
        emptyState={
          <>
            <i className="fas fa-search"></i>
            <p>No analysis reports found.</p>
          </>
        }
        loading={loading}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(limit) => {
          setItemsPerPage(limit);
          setCurrentPage(1);
        }}
        totalItems={filteredAndSortedReports.length}
        itemsName="reports"
      />
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default AnalysisReportView;
